<template>
  <div class="weekly-projection-approval pa-4">
    <!-- Header / Title & Summary Counters Card -->
    <v-card class="mb-4 rounded-xl filter-card elevation-2" outlined>
      <v-card-title class="d-flex align-center py-3 px-5 bg-header flex-wrap">
        <v-icon color="primary" large class="mr-3"
          >mdi-checkbox-marked-circle-outline</v-icon
        >
        <div class="mr-4">
          <h2 class="text-h6 font-weight-black primary--text mb-0">
            Aprobación de Programación Semanal
          </h2>
          <span class="text-caption grey--text text--darken-1">
            Revisión y aprobación masiva o individual por Campaña, Semana, Supervisor y Tipo de Negociación
          </span>
        </div>

        <v-spacer></v-spacer>

        <!-- Current Approval Status Chips -->
        <div class="d-flex align-center gap-2 my-1 flex-wrap" v-if="approvalGroups.length > 0">
          <v-chip
            small
            class="font-weight-black px-3 white--text"
            color="warning darken-2"
          >
            <v-icon left x-small color="white">mdi-clock-outline</v-icon>
            {{ countPending }} Pendientes
          </v-chip>

          <v-chip
            small
            class="font-weight-black px-3 white--text"
            color="success darken-1"
          >
            <v-icon left x-small color="white">mdi-check-circle</v-icon>
            {{ countApproved }} Aprobados
          </v-chip>

          <v-chip
            v-if="countRejected > 0"
            small
            class="font-weight-black px-3 white--text"
            color="error darken-1"
          >
            <v-icon left x-small color="white">mdi-close-circle</v-icon>
            {{ countRejected }} Rechazados
          </v-chip>
        </div>
      </v-card-title>
    </v-card>

    <!-- Dedicated Filter Card -->
    <v-card class="mb-5 rounded-xl filter-card elevation-2" outlined>
      <v-card-title class="py-2 px-5 bg-filter-header d-flex align-center">
        <v-icon color="primary" small class="mr-2">mdi-filter-variant</v-icon>
        <span
          class="text-subtitle-2 font-weight-black primary--text text-uppercase"
        >
          Filtros de Búsqueda
        </span>
        <v-spacer></v-spacer>
        <span class="text-caption grey--text text--darken-1">
          Filtra por Campaña, Semana, Supervisor, Negociación, Productor o Cultivo
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-3 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <s-select-campaign-area
              label="Campaña *"
              v-model="filters.cmaId"
              clearable
              typeArea="1"
              full
              @change="loadProjections"
            />
          </v-col>
          <v-col cols="12" sm="4" md="2">
            <s-select
              label="Año"
              :items="years"
              outlined
              hide-details
              dense
              v-model="filters.year"
              clearable
              @change="loadProjections"
            />
          </v-col>
          <v-col cols="12" sm="4" md="2">
            <s-periods
              label="Semana *"
              v-model="selectedWeekObj"
              :value-id="filters.pdsId"
              :year="filters.year"
              :type="1"
              return-object
              clearable
              @input="onWeekChange"
            />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <s-select-supervice
              full
              clearable
              label="Supervisor"
              v-model="filters.prsSuperviceId"
              @change="loadProjections"
            />
          </v-col>
          <v-col cols="12" sm="12" md="2" class="text-right">
            <v-btn
              block
              color="primary"
              class="rounded-lg font-weight-bold white--text"
              dense
              elevation="1"
              @click="loadProjections"
              :loading="loading"
            >
              <v-icon left small>mdi-magnify</v-icon>
              Cargar
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <s-select-producer
              full
              clearable
              noDefault
              label="Productor"
              v-model="filters.prdId"
              @change="loadProjections"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <s-select-definition
              clearable
              :def="1173"
              label="Cultivo"
              v-model="filters.typeCultive"
              @change="loadProjections"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <s-select-variety
              :cultiveID="filters.typeCultive"
              label="Variedad"
              full
              clearable
              v-model="filters.vrtId"
              @change="loadProjections"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <s-select-definition
              clearable
              :def="1610"
              label="Tipo Negociación"
              v-model="filters.typeNegociation"
              @change="loadProjections"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- KPI Summary Strip (Neutro Plano sin Degradados) -->
    <v-row dense class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-mini-card pa-3 d-flex align-center" outlined>
          <v-avatar color="blue lighten-5" size="42" class="mr-3">
            <v-icon color="blue darken-2">mdi-cube-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
              Meta Semanal
            </div>
            <div class="text-h6 font-weight-black blue--text text--darken-3">
              {{ totalProjectedJabas | formatNumber }} jabas
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-mini-card pa-3 d-flex align-center" outlined>
          <v-avatar color="green lighten-5" size="42" class="mr-3">
            <v-icon color="green darken-2">mdi-calendar-check</v-icon>
          </v-avatar>
          <div>
            <div class="text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
              Total Asignado
            </div>
            <div class="text-h6 font-weight-black green--text text--darken-3">
              {{ totalAssignedJabas | formatNumber }} jabas
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-mini-card pa-3 d-flex align-center" outlined>
          <v-avatar color="purple lighten-5" size="42" class="mr-3">
            <v-icon color="purple darken-2">mdi-account-supervisor-circle</v-icon>
          </v-avatar>
          <div>
            <div class="text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
              Grupos / Supervisores
            </div>
            <div class="text-h6 font-weight-black purple--text text--darken-3">
              {{ approvalGroups.length }} grupos ({{ supervisorCount }} sup.)
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-mini-card pa-3 d-flex align-center" outlined>
          <v-avatar color="amber lighten-5" size="42" class="mr-3">
            <v-icon color="amber darken-3">mdi-shield-check-outline</v-icon>
          </v-avatar>
          <div>
            <div class="text-caption font-weight-bold grey--text text--darken-1 text-uppercase">
              Aprobación General
            </div>
            <div class="text-h6 font-weight-black amber--text text--darken-4">
              {{ countApproved }} de {{ approvalGroups.length }} aprobados
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- SECCIÓN 1: Tabla de Programaciones para Aprobación Masiva e Individual -->
    <v-card class="rounded-xl elevation-2 mb-5 filter-card" outlined>
      <v-card-title class="py-3 px-4 bg-header d-flex align-center flex-wrap">
        <v-icon color="primary" class="mr-2">mdi-account-group-outline</v-icon>
        <div>
          <span class="text-subtitle-2 font-weight-black primary--text text-uppercase">
            Programaciones por Supervisor y Negociación
          </span>
          <span class="text-caption grey--text text--darken-1 ml-2">
            ({{ approvalGroups.length }} programaciones en este período)
          </span>
        </div>

        <v-spacer></v-spacer>

        <!-- Botonera de Acción Masiva -->
        <div class="d-flex align-center gap-2 flex-wrap my-1">
          <v-chip
            v-if="selectedGroups.length > 0"
            small
            color="primary"
            outlined
            class="font-weight-bold"
          >
            {{ selectedGroups.length }} seleccionado(s)
          </v-chip>

          <v-btn
            color="success"
            small
            class="rounded-lg font-weight-bold white--text"
            elevation="1"
            :disabled="selectedGroups.length === 0 || submitting"
            :loading="submitting"
            @click="approveBulk(2)"
          >
            <v-icon left small>mdi-check-all</v-icon>
            Aprobar Seleccionados
          </v-btn>

          <v-btn
            color="error"
            small
            class="rounded-lg font-weight-bold white--text"
            elevation="1"
            :disabled="selectedGroups.length === 0 || submitting"
            :loading="submitting"
            @click="approveBulk(3)"
          >
            <v-icon left small>mdi-close-octagon-outline</v-icon>
            Rechazar Seleccionados
          </v-btn>

          <v-btn
            v-if="countPending > 0"
            color="primary"
            outlined
            small
            class="rounded-lg font-weight-bold"
            :disabled="submitting"
            @click="approveAllPending"
          >
            <v-icon left small>mdi-playlist-check</v-icon>
            Aprobar Todos Pendientes ({{ countPending }})
          </v-btn>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Grilla de Grupos -->
      <v-data-table
        show-select
        v-model="selectedGroups"
        :headers="groupHeaders"
        :items="approvalGroups"
        item-key="groupKey"
        dense
        :loading="loading"
        loading-text="Cargando programaciones..."
        no-data-text="No se encontraron programaciones para los filtros seleccionados."
        class="elevation-0 group-approval-table"
      >
        <!-- Columna Supervisor -->
        <template v-slot:item.PrsSuperviceName="{ item }">
          <div class="d-flex align-center py-1">
            <v-avatar size="28" color="grey lighten-3" class="mr-2">
              <v-icon small color="grey darken-2">mdi-account</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold text-caption black--text">
                {{ item.PrsSuperviceName }}
              </div>
              <div class="text-xxs grey--text">
                WpcID: {{ item.WpcID || 'No generado' }}
              </div>
            </div>
          </div>
        </template>

        <!-- Columna Tipo Negociación -->
        <template v-slot:item.TypeNegociationName="{ item }">
          <v-chip
            x-small
            outlined
            color="indigo darken-1"
            class="font-weight-bold"
          >
            {{ item.TypeNegociationName }}
          </v-chip>
        </template>

        <!-- Columna Lotes -->
        <template v-slot:item.lotCount="{ item }">
          <span class="font-weight-bold text-caption">
            {{ item.lotCount }} lotes
          </span>
        </template>

        <!-- Columna Jabas -->
        <template v-slot:item.totalJabas="{ item }">
          <span class="font-weight-bold text-caption primary--text">
            {{ item.totalJabas | formatNumber }} j.
          </span>
        </template>

        <!-- Columna Estado -->
        <template v-slot:item.StatusApproval="{ item }">
          <v-chip
            x-small
            class="font-weight-bold white--text"
            :color="getStatusColor(item.StatusApproval)"
          >
            <v-icon left x-small color="white">{{ getStatusIcon(item.StatusApproval) }}</v-icon>
            {{ getStatusText(item.StatusApproval) }}
          </v-chip>
        </template>

        <!-- Columna Observación -->
        <template v-slot:item.WpcObservation="{ item }">
          <span v-if="item.WpcObservation" class="text-caption grey--text text--darken-2 text-truncate d-inline-block" style="max-width: 220px;" :title="item.WpcObservation">
            {{ item.WpcObservation }}
          </span>
          <span v-else class="text-caption grey--text text--lighten-1">Sin observaciones</span>
        </template>

        <!-- Columna Auditoría -->
        <template v-slot:item.audit="{ item }">
          <div v-if="item.SecApprovalDate" class="text-xxs">
            <div class="grey--text text--darken-2 font-weight-bold">
              {{ formatDateTime(item.SecApprovalDate) }}
            </div>
            <div class="grey--text">
              Usr: {{ item.UsrApprovalID || 'N/A' }}
            </div>
          </div>
          <span v-else class="text-caption grey--text">-</span>
        </template>

        <!-- Columna Acciones Individuales -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center justify-center gap-1">
            <!-- Ver / Filtrar distribución -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :color="activeGroup && activeGroup.groupKey === item.groupKey ? 'primary' : 'grey darken-1'"
                  @click="toggleActiveGroup(item)"
                >
                  <v-icon small>
                    {{ activeGroup && activeGroup.groupKey === item.groupKey ? 'mdi-eye-check' : 'mdi-eye-outline' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ activeGroup && activeGroup.groupKey === item.groupKey ? 'Viendo esta distribución' : 'Ver detalle de este grupo' }}</span>
            </v-tooltip>

            <!-- Aprobar individual -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="success"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="item.StatusApproval === 2 || !item.WpcID || submitting"
                  @click="approveSingle(item, 2)"
                >
                  <v-icon small>mdi-check-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>Aprobar este grupo</span>
            </v-tooltip>

            <!-- Rechazar individual -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="item.StatusApproval === 3 || !item.WpcID || submitting"
                  @click="approveSingle(item, 3)"
                >
                  <v-icon small>mdi-close-circle-outline</v-icon>
                </v-btn>
              </template>
              <span>Rechazar este grupo</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- SECCIÓN 2: Resumen Matriz de Distribución Semanal -->
    <v-card class="rounded-xl elevation-2 mb-5 filter-card" outlined>
      <v-card-title class="py-3 px-4 bg-table-header d-flex align-center flex-wrap">
        <v-icon color="primary" class="mr-2">mdi-table-large</v-icon>
        <div>
          <span class="text-subtitle-2 font-weight-black primary--text text-uppercase">
            {{ activeGroup ? `Distribución Diaria: ${activeGroup.PrsSuperviceName} - ${activeGroup.TypeNegociationName}` : 'Distribución General de Asignaciones Diarias' }}
          </span>
          <span class="text-caption grey--text text--darken-1 ml-2">
            ({{ groupedLotSummary.length }} lotes programados)
          </span>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          v-if="activeGroup"
          x-small
          outlined
          color="primary"
          class="rounded-lg font-weight-bold mr-2"
          @click="activeGroup = null"
        >
          <v-icon left x-small>mdi-filter-remove</v-icon>
          Ver Todas las Distribuciones
        </v-btn>
        <v-chip x-small color="indigo" class="white--text font-weight-bold">
          {{ totalAssignedFiltered | formatNumber }} jabas
        </v-chip>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Tabla de distribución -->
      <v-card-text class="pa-0">
        <v-simple-table dense class="weekly-summary-table">
          <template v-slot:default>
            <thead>
              <tr class="bg-light-grey">
                <th class="text-left font-weight-black">Lote / Productor</th>
                <th class="text-left font-weight-black">Fundo</th>
                <th class="text-left font-weight-black">Cultivo / Var.</th>
                <th class="text-right font-weight-black">Meta Total</th>
                <th
                  v-for="day in days"
                  :key="day.id"
                  class="text-center font-weight-black day-header-cell"
                >
                  <div>{{ day.name }}</div>
                  <div class="text-xxs grey--text font-weight-normal">
                    {{ formatDateShort(day.date) }}
                  </div>
                </th>
                <th class="text-right font-weight-black primary--text">
                  Total Prog.
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="lot in groupedLotSummary" :key="lot.WptID">
                <td class="font-weight-bold text-caption">
                  <div>{{ lot.FltName }}</div>
                  <div class="text-xxs grey--text text--darken-1">
                    {{ lot.PrdCardName }}
                  </div>
                </td>
                <td class="text-caption grey--text text--darken-2">
                  {{ lot.FagName }}
                </td>
                <td>
                  <v-chip
                    x-small
                    :color="getCropColor(lot.TypeCultive)"
                    class="white--text font-weight-bold"
                  >
                    {{ lot.TypeCultive }}
                  </v-chip>
                  <div class="text-xxs grey--text mt-0-5">
                    {{ lot.VrtDescription }}
                  </div>
                </td>
                <td class="text-right font-weight-bold text-caption">
                  {{ lot.WptProjectedJabas | formatNumber }} j.
                </td>
                <!-- 7 días (Lunes a Domingo) -->
                <td
                  v-for="day in days"
                  :key="day.id"
                  class="text-center font-weight-bold text-caption"
                  :class="{ 'day-has-value': lot.dayQuantities[day.date] > 0 }"
                >
                  <span v-if="lot.dayQuantities[day.date] > 0">
                    {{ lot.dayQuantities[day.date] | formatNumber }}
                  </span>
                  <span v-else class="grey--text text--lighten-2">-</span>
                </td>
                <td class="text-right font-weight-black primary--text text-caption">
                  {{ lot.totalScheduled | formatNumber }} j.
                </td>
              </tr>

              <tr v-if="groupedLotSummary.length === 0">
                <td colspan="12" class="text-center py-5 grey--text">
                  <v-icon large color="grey lighten-1" class="d-block mb-1"
                    >mdi-calendar-remove</v-icon
                  >
                  No hay asignaciones registradas para el período seleccionado.
                </td>
              </tr>
            </tbody>
            <tfoot v-if="groupedLotSummary.length > 0">
              <tr class="bg-light-primary">
                <td colspan="3" class="font-weight-black primary--text text-subtitle-2">
                  TOTALES
                </td>
                <td class="text-right font-weight-black primary--text text-caption">
                  {{ totalProjectedFiltered | formatNumber }} j.
                </td>
                <td
                  v-for="day in days"
                  :key="day.id"
                  class="text-center font-weight-black primary--text text-caption"
                >
                  {{ getDayTotalJabasFiltered(day) | formatNumber }}
                </td>
                <td class="text-right font-weight-black success--text text-subtitle-2">
                  {{ totalAssignedFiltered | formatNumber }} j.
                </td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <!-- Modal para Observaciones de Dictamen (Individual o Masivo) -->
    <v-dialog v-model="modalObs.open" max-width="520px" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="py-3 px-4 bg-header d-flex align-center">
          <v-icon
            :color="modalObs.targetStatus === 3 ? 'error' : 'success'"
            class="mr-2"
          >
            {{ modalObs.targetStatus === 3 ? 'mdi-alert-circle' : 'mdi-check-circle' }}
          </v-icon>
          <span class="text-subtitle-1 font-weight-black">
            {{ modalObs.targetStatus === 3 ? 'Rechazar Programación' : 'Aprobar Programación' }}
          </span>
          <v-spacer></v-spacer>
          <v-btn icon small @click="modalObs.open = false" :disabled="submitting">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <p class="text-caption mb-3 grey--text text--darken-3">
            <span v-if="modalObs.isBulk">
              Va a <strong>{{ modalObs.targetStatus === 3 ? 'rechazar' : 'aprobar' }}</strong> <strong>{{ modalObs.targetGroups.length }}</strong> programación(es) seleccionada(s).
            </span>
            <span v-else-if="modalObs.targetGroups[0]">
              Va a <strong>{{ modalObs.targetStatus === 3 ? 'rechazar' : 'aprobar' }}</strong> la programación de <strong>{{ modalObs.targetGroups[0].PrsSuperviceName }}</strong> ({{ modalObs.targetGroups[0].TypeNegociationName }}).
            </span>
          </p>

          <s-textarea
            v-model="modalObs.observation"
            label="Observación o Motivo del Dictamen *"
            :rows="3"
            placeholder="Escriba aquí cualquier observación, justificación o requerimiento especial..."
          />
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="py-3 px-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="rounded-lg font-weight-bold"
            @click="modalObs.open = false"
            :disabled="submitting"
          >
            Cancelar
          </v-btn>
          <v-btn
            :color="modalObs.targetStatus === 3 ? 'error' : 'success'"
            class="rounded-lg font-weight-bold white--text px-4"
            :loading="submitting"
            @click="confirmModalApproval"
          >
            Confirmar {{ modalObs.targetStatus === 3 ? 'Rechazo' : 'Aprobación' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import WeeklyProjectionCalendarService from "@/services/Technicalassistance/WeeklyProjectionCalendarService";
import DefinitionService from "@/services/General/DefinitionService";
import SSelectProducer from "@/components/HarvestSchedule/SSelectProducer.vue";
import SSelectVariety from "@/components/HarvestSchedule/SSelectVariety.vue";
import SSelectSupervice from "@/components/HarvestSchedule/SSelectSupervice";

export default {
  name: "WeeklyProjectionApproval",
  components: {
    SSelectProducer,
    SSelectVariety,
    SSelectSupervice,
  },
  filters: {
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return new Intl.NumberFormat("es-PE").format(Math.round(val));
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      years: [],
      selectedWeekObj: null,
      itemsTypeNegociation: [],
      selectedGroups: [],
      activeGroup: null,
      approvalGroups: [],
      modalObs: {
        open: false,
        isBulk: false,
        targetStatus: 2,
        targetGroups: [],
        observation: "",
      },
      filters: {
        year: new Date().getFullYear(),
        pdsId: null,
        cmaId: null,
        prdId: null,
        prsSuperviceId: null,
        typeCultive: 0,
        vrtId: 0,
        typeNegociation: null,
      },
      groupHeaders: [
        { text: "Supervisor", value: "PrsSuperviceName", sortable: true },
        { text: "Tipo Negociación", value: "TypeNegociationName", sortable: true },
        { text: "Lotes Prog.", value: "lotCount", align: "center", sortable: true },
        { text: "Total Meta Jabas", value: "totalJabas", align: "right", sortable: true },
        { text: "Estado", value: "StatusApproval", align: "center", sortable: true },
        { text: "Observación", value: "WpcObservation", sortable: false },
        { text: "Auditoría", value: "audit", sortable: false },
        { text: "Acciones", value: "actions", align: "center", sortable: false, width: "150px" },
      ],
      days: [
        { id: 1, name: "LUNES", date: "" },
        { id: 2, name: "MARTES", date: "" },
        { id: 3, name: "MIÉRCOLES", date: "" },
        { id: 4, name: "JUEVES", date: "" },
        { id: 5, name: "VIERNES", date: "" },
        { id: 6, name: "SÁBADO", date: "" },
        { id: 7, name: "DOMINGO", date: "" },
      ],
      calendarDetails: [],
      allProjections: [],
    };
  },
  computed: {
    countPending() {
      return this.approvalGroups.filter(
        (g) => Number(g.StatusApproval) === 1
      ).length;
    },
    countApproved() {
      return this.approvalGroups.filter(
        (g) => Number(g.StatusApproval) === 2
      ).length;
    },
    countRejected() {
      return this.approvalGroups.filter(
        (g) => Number(g.StatusApproval) === 3
      ).length;
    },
    supervisorCount() {
      return new Set(this.approvalGroups.map((g) => g.PrsSuperviceID)).size;
    },
    totalProjectedJabas() {
      return this.allProjections.reduce(
        (sum, p) => sum + Number(p.WptProjectedJabas || 0),
        0
      );
    },
    totalAssignedJabas() {
      return this.calendarDetails.reduce(
        (sum, d) => sum + Number(d.WpdQuantity || 0),
        0
      );
    },
    totalProjectedFiltered() {
      return this.groupedLotSummary.reduce(
        (sum, lot) => sum + Number(lot.WptProjectedJabas || 0),
        0
      );
    },
    totalAssignedFiltered() {
      return this.groupedLotSummary.reduce(
        (sum, lot) => sum + Number(lot.totalScheduled || 0),
        0
      );
    },
    groupedLotSummary() {
      const map = new Map();
      const detailsToUse = this.activeGroup
        ? this.calendarDetails.filter((d) => {
            if (this.activeGroup.WpcID > 0 && d.WpcID > 0) {
              return d.WpcID === this.activeGroup.WpcID;
            }
            return (
              (d.PrsSuperviceID || 0) === (this.activeGroup.PrsSuperviceID || 0) &&
              (d.TypeNegociation || 0) === (this.activeGroup.TypeNegociation || 0)
            );
          })
        : this.calendarDetails;

      detailsToUse.forEach((row) => {
        const wptId = row.WptID;
        if (!map.has(wptId)) {
          const dayQuantities = {};
          this.days.forEach((day) => (dayQuantities[day.date] = 0));

          map.set(wptId, {
            WptID: wptId,
            FltName: row.FltName || `Lote ${row.FltID}`,
            PrdCardName: row.PrdCardName || "Sin Productor",
            FagName: row.FagName || "Fundo no especificado",
            TypeCultive: row.TypeCultive || "Cultivo",
            VrtDescription: row.VrtDescription || "-",
            WptProjectedJabas: Number(row.WptProjectedJabas || 0),
            dayQuantities,
            totalScheduled: 0,
          });
        }

        const lotObj = map.get(wptId);
        let dateStr = "";
        const rawDate = row.WpdDate || row.Date;
        if (rawDate) {
          dateStr = typeof rawDate === "string" ? rawDate.split("T")[0] : rawDate;
        }

        if (dateStr && lotObj.dayQuantities[dateStr] !== undefined) {
          lotObj.dayQuantities[dateStr] += Number(row.WpdQuantity || 0);
          lotObj.totalScheduled += Number(row.WpdQuantity || 0);
        }
      });

      return Array.from(map.values());
    },
  },
  created() {
    this.years = this.$fun?.getNext3Years?.() || [
      this.filters.year,
      this.filters.year + 1,
    ];
    this.calculateWeekDates();
    this.loadTypeNegociations();
  },
  methods: {
    async loadTypeNegociations() {
      try {
        const res = await DefinitionService.definitiongroup(
          1610,
          0,
          this.$fun?.getUserID?.() || 1
        );
        if (res && res.data && res.data.length > 0) {
          this.itemsTypeNegociation = res.data;
        }
      } catch (e) {
        console.error("Error al cargar tipos de negociación:", e);
      }
    },

    formatDateShort(dateStr) {
      if (!dateStr) return "";
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}`;
      }
      return dateStr;
    },

    formatDateTime(dateVal) {
      if (!dateVal) return "-";
      try {
        const d = new Date(dateVal);
        return d.toLocaleString("es-PE");
      } catch (e) {
        return String(dateVal);
      }
    },

    getCropColor(cropName) {
      if (!cropName) return "grey";
      const name = String(cropName).toLowerCase();
      if (name.includes("mango")) return "orange darken-2";
      if (name.includes("palto") || name.includes("aguacate"))
        return "green darken-2";
      if (name.includes("arandano") || name.includes("berry"))
        return "indigo darken-2";
      if (name.includes("uva")) return "purple darken-2";
      if (name.includes("limon") || name.includes("citrico"))
        return "lime darken-3";
      return "primary";
    },

    getStatusText(status) {
      switch (Number(status)) {
        case 2:
          return "Aprobado";
        case 3:
          return "Rechazado";
        case 1:
        default:
          return "Pendiente";
      }
    },

    getStatusColor(status) {
      switch (Number(status)) {
        case 2:
          return "success darken-1";
        case 3:
          return "error darken-1";
        case 1:
        default:
          return "warning darken-2";
      }
    },

    getStatusIcon(status) {
      switch (Number(status)) {
        case 2:
          return "mdi-check-circle";
        case 3:
          return "mdi-close-circle";
        case 1:
        default:
          return "mdi-clock-outline";
      }
    },

    getDayTotalJabasFiltered(day) {
      const detailsToUse = this.activeGroup
        ? this.calendarDetails.filter((d) => {
            if (this.activeGroup.WpcID > 0 && d.WpcID > 0) {
              return d.WpcID === this.activeGroup.WpcID;
            }
            return (
              (d.PrsSuperviceID || 0) === (this.activeGroup.PrsSuperviceID || 0) &&
              (d.TypeNegociation || 0) === (this.activeGroup.TypeNegociation || 0)
            );
          })
        : this.calendarDetails;

      return detailsToUse
        .filter((d) => {
          const rawDate = d.WpdDate || d.Date;
          const dateStr =
            typeof rawDate === "string" ? rawDate.split("T")[0] : rawDate;
          return dateStr === day.date;
        })
        .reduce((sum, item) => sum + Number(item.WpdQuantity || 0), 0);
    },

    onWeekChange(weekObj) {
      if (!weekObj) return;
      this.selectedWeekObj = weekObj;
      this.filters.pdsId = weekObj.PdsID || null;
      if (weekObj.PdsDateBegin) {
        this.calculateWeekDates(weekObj.PdsDateBegin);
      }
      this.loadProjections();
    },

    calculateWeekDates(startDateStr) {
      let baseDate;
      if (startDateStr) {
        const dateOnly = String(startDateStr).split("T")[0];
        const parts = dateOnly.split("-").map(Number);
        if (parts.length === 3) {
          baseDate = new Date(parts[0], parts[1] - 1, parts[2]);
        } else {
          baseDate = new Date(startDateStr);
        }
      } else {
        baseDate = new Date();
      }

      const dayOfWeek = baseDate.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      baseDate.setDate(baseDate.getDate() + diffToMonday);

      this.days.forEach((day, index) => {
        const d = new Date(baseDate);
        d.setDate(baseDate.getDate() + index);

        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        day.date = `${yyyy}-${mm}-${dd}`;
      });
    },

    async loadProjections() {
      const pdsIdVal =
        this.filters.pdsId ||
        (this.selectedWeekObj
          ? typeof this.selectedWeekObj === "object"
            ? this.selectedWeekObj.PdsID
            : this.selectedWeekObj
          : null);

      if (!this.filters.cmaId || !pdsIdVal) {
        this.calendarDetails = [];
        this.allProjections = [];
        this.approvalGroups = [];
        this.selectedGroups = [];
        this.activeGroup = null;
        return;
      }

      this.loading = true;
      try {
        const requestID = this.$fun?.getUserID?.() || 1;

        const filterPayload = {
          CmaID: Number(this.filters.cmaId),
          PrdID: this.filters.prdId ? Number(this.filters.prdId) : null,
          PdsID: Number(pdsIdVal),
          TypeCultive: this.filters.typeCultive
            ? Number(this.filters.typeCultive)
            : null,
          VrtID: this.filters.vrtId ? Number(this.filters.vrtId) : null,
          PrsSuperviceID: this.filters.prsSuperviceId
            ? Number(this.filters.prsSuperviceId)
            : null,
          TypeNegociation: this.filters.typeNegociation
            ? Number(this.filters.typeNegociation)
            : null,
        };

        const [respUnassigned, respCalendar] = await Promise.all([
          WeeklyProjectionCalendarService.ListProjection(
            filterPayload,
            requestID
          ).catch((e) => null),
          WeeklyProjectionCalendarService.ListProjectionCalendar(
            filterPayload,
            requestID
          ).catch((e) => null),
        ]);

        const extractRows = (resp) => {
          if (resp?.status === 200 && resp?.data) {
            if (Array.isArray(resp.data)) return resp.data;
            if (Array.isArray(resp.data.data)) return resp.data.data;
            if (Array.isArray(resp.data.items)) return resp.data.items;
            if (Array.isArray(resp.data.result)) return resp.data.result;
            if (typeof resp.data === "object") {
              const arr = Object.values(resp.data).find((val) =>
                Array.isArray(val)
              );
              if (arr) return arr;
            }
          }
          return [];
        };

        this.allProjections = extractRows(respUnassigned);
        this.calendarDetails = extractRows(respCalendar);

        // Agrupar filas del calendario por la combinación (CmaID, PdsID, PrsSuperviceID, TypeNegociation) o WpcID
        const groupMap = new Map();
        (this.calendarDetails || []).forEach((row) => {
          const wpcId = row.WpcID || 0;
          const supId =
            row.PrsSuperviceID !== null && row.PrsSuperviceID !== undefined
              ? row.PrsSuperviceID
              : 0;
          const negId =
            row.TypeNegociation !== null && row.TypeNegociation !== undefined
              ? row.TypeNegociation
              : 0;
          const groupKey =
            wpcId > 0 ? `wpc_${wpcId}` : `grp_${supId}_${negId}`;

          if (!groupMap.has(groupKey)) {
            let negName = row.TypeNegociationName;
            if (
              !negName &&
              this.itemsTypeNegociation &&
              this.itemsTypeNegociation.length > 0
            ) {
              const found = this.itemsTypeNegociation.find(
                (x) => Number(x.DedValue) === Number(row.TypeNegociation)
              );
              if (found) negName = found.DedDescription;
            }

            groupMap.set(groupKey, {
              groupKey,
              WpcID: wpcId,
              CmaID: row.CmaID || Number(this.filters.cmaId),
              PdsID: row.PdsID || Number(pdsIdVal),
              PrsSuperviceID: row.PrsSuperviceID || null,
              PrsSuperviceName:
                row.PrsSuperviceName ||
                (row.PrsSuperviceID
                  ? `Supervisor #${row.PrsSuperviceID}`
                  : "Sin Supervisor"),
              TypeNegociation:
                row.TypeNegociation !== undefined ? row.TypeNegociation : null,
              TypeNegociationName:
                negName ||
                (row.TypeNegociation !== null &&
                row.TypeNegociation !== undefined
                  ? `Tipo ${row.TypeNegociation}`
                  : "No asignado"),
              StatusApproval: Number(row.StatusApproval || 1),
              WpcObservation: row.WpcObservation || "",
              UsrApprovalID: row.UsrApprovalID || null,
              SecApprovalDate: row.SecApprovalDate || null,
              UUID: row.UUID || null,
              totalJabas: 0,
              lotSet: new Set(),
            });
          }

          const grp = groupMap.get(groupKey);
          grp.totalJabas += Number(row.WpdQuantity || 0);
          if (row.WptID) grp.lotSet.add(row.WptID);
        });

        this.approvalGroups = Array.from(groupMap.values()).map((g) => ({
          ...g,
          lotCount: g.lotSet.size,
        }));

        this.selectedGroups = [];
        if (this.activeGroup) {
          const match = this.approvalGroups.find(
            (g) => g.groupKey === this.activeGroup.groupKey
          );
          this.activeGroup = match || null;
        }
      } catch (error) {
        console.error("Error al cargar proyecciones para aprobación:", error);
        this.showToast("Error al cargar las proyecciones", "error");
      } finally {
        this.loading = false;
      }
    },

    toggleActiveGroup(item) {
      if (this.activeGroup && this.activeGroup.groupKey === item.groupKey) {
        this.activeGroup = null;
      } else {
        this.activeGroup = item;
      }
    },

    async askConfirm(message) {
      if (this.$fun?.alert) {
        const res = await this.$fun.alert(message, "question");
        return res && res.value;
      }
      return window.confirm(message);
    },

    showToast(msg, type = "success") {
      if (this.$fun?.alert) {
        this.$fun.alert(msg, type);
      }
    },

    async approveSingle(group, targetStatus = 2) {
      if (!group || !group.WpcID) {
        this.showToast(
          "Esta programación no cuenta con una cabecera guardada en el calendario.",
          "warning"
        );
        return;
      }

      if (targetStatus === 3) {
        this.modalObs = {
          open: true,
          isBulk: false,
          targetStatus: 3,
          targetGroups: [group],
          observation: group.WpcObservation || "",
        };
        return;
      }

      const actionText = targetStatus === 2 ? "Aprobar" : "Procesar";
      const confirm = await this.askConfirm(
        `¿Desea ${actionText} la programación de ${group.PrsSuperviceName} (${group.TypeNegociationName})?`
      );
      if (!confirm) return;

      await this.processApproval([group], targetStatus, group.WpcObservation || "");
    },

    async approveBulk(targetStatus = 2) {
      if (!this.selectedGroups || this.selectedGroups.length === 0) {
        this.showToast(
          "Seleccione al menos una programación de la tabla con los checkboxes.",
          "warning"
        );
        return;
      }

      const validGroups = this.selectedGroups.filter((g) => g.WpcID > 0);
      if (validGroups.length === 0) {
        this.showToast(
          "Las programaciones seleccionadas no tienen registro guardado para procesar.",
          "warning"
        );
        return;
      }

      if (targetStatus === 3) {
        this.modalObs = {
          open: true,
          isBulk: true,
          targetStatus: 3,
          targetGroups: validGroups,
          observation: "",
        };
        return;
      }

      const actionText = targetStatus === 2 ? "Aprobar" : "Procesar";
      const confirm = await this.askConfirm(
        `¿Desea ${actionText} de manera MASIVA las ${validGroups.length} programaciones seleccionadas?`
      );
      if (!confirm) return;

      await this.processApproval(validGroups, targetStatus, "");
    },

    async approveAllPending() {
      const pendingGroups = this.approvalGroups.filter(
        (g) => Number(g.StatusApproval) === 1 && g.WpcID > 0
      );
      if (pendingGroups.length === 0) {
        this.showToast(
          "No hay programaciones pendientes guardadas para aprobar.",
          "info"
        );
        return;
      }

      const confirm = await this.askConfirm(
        `¿Desea APROBAR TODOS los ${pendingGroups.length} grupos pendientes de esta semana?`
      );
      if (!confirm) return;

      await this.processApproval(pendingGroups, 2, "");
    },

    async confirmModalApproval() {
      if (
        this.modalObs.targetStatus === 3 &&
        (!this.modalObs.observation ||
          !String(this.modalObs.observation).trim())
      ) {
        this.showToast("Por favor ingrese el motivo de rechazo.", "warning");
        return;
      }

      await this.processApproval(
        this.modalObs.targetGroups,
        this.modalObs.targetStatus,
        this.modalObs.observation
      );
    },

    async processApproval(groups, targetStatus, observation) {
      this.submitting = true;
      try {
        const currentUserId = this.$fun?.getUserID?.() || 1;
        const now = new Date();

        const payloadList = groups.map((g) => ({
          WpcID: Number(g.WpcID),
          CmaID: Number(g.CmaID),
          PdsID: Number(g.PdsID),
          SecStatus: 1,
          StatusApproval: Number(targetStatus),
          WpcObservation:
            observation !== undefined &&
            observation !== null &&
            String(observation).trim().length > 0
              ? String(observation).trim()
              : g.WpcObservation || null,
          PrsSuperviceID: g.PrsSuperviceID ? Number(g.PrsSuperviceID) : null,
          TypeNegociation:
            g.TypeNegociation !== null && g.TypeNegociation !== undefined
              ? Number(g.TypeNegociation)
              : null,
          UUID: g.UUID || null,
          UsrApprovalID: currentUserId,
          SecApprovalDate: now,
          UsrUpdateID: currentUserId,
        }));

        if (payloadList.length === 1) {
          await WeeklyProjectionCalendarService.save(
            payloadList[0],
            currentUserId
          );
        } else {
          await WeeklyProjectionCalendarService.saveList(
            payloadList,
            currentUserId
          );
        }

        const actionText =
          targetStatus === 2
            ? "Aprobada(s)"
            : targetStatus === 3
            ? "Rechazada(s)"
            : "Actualizada(s)";

        this.showToast(
          `¡${payloadList.length} programación(es) ${actionText.toLowerCase()} con éxito!`,
          "success"
        );

        this.selectedGroups = [];
        this.modalObs.open = false;
        await this.loadProjections();
      } catch (error) {
        console.error("Error al procesar dictamen:", error);
        this.showToast("Error al procesar el dictamen de aprobación", "error");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.weekly-projection-approval {
  background-color: #f8fafc;
  min-height: 100vh;
}

.filter-card {
  background: white !important;
  border: 1px solid #e2e8f0 !important;
}

.bg-header {
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.bg-filter-header {
  background-color: #f8fafc;
}

.bg-table-header {
  background-color: #f8fafc;
}

.bg-light-grey {
  background-color: #f8fafc;
}

.bg-light-primary {
  background-color: #eff6ff;
}

.kpi-mini-card {
  height: 80px;
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px;
}

.weekly-summary-table {
  border-collapse: collapse;
  width: 100%;
}

.weekly-summary-table th {
  padding: 10px 8px !important;
  font-size: 11px !important;
  border-bottom: 2px solid #cbd5e1 !important;
}

.weekly-summary-table td {
  padding: 8px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.day-header-cell {
  min-width: 75px;
}

.day-has-value {
  background-color: #f0fdf4 !important;
  color: #15803d !important;
}

.text-xxs {
  font-size: 10px;
  line-height: 1.2;
}

.mt-0-5 {
  margin-top: 2px;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}
</style>
